import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { validatePassword, validatePasswordConfirmation } from '@/lib/validator';

type ResetPasswordForm = {
    password: string;
    confirmPassword: string;
};

const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const { resetPassword } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<ResetPasswordForm>();

    const password = watch('password', '');

    const onSubmit = async (data: ResetPasswordForm) => {
        // Validazione manuale usando i validator esistenti
        const passwordValidation = validatePassword(data.password);
        if (!passwordValidation.isValid) {
            toast.error(passwordValidation.message);
            return;
        }

        const confirmValidation = validatePasswordConfirmation(data.password, data.confirmPassword);
        if (!confirmValidation.isValid) {
            toast.error(confirmValidation.message);
            return;
        }

        setLoading(true);
        try {
            const res = await resetPassword(data.password);
            if (res.success) {
                toast.success('Password aggiornata con successo');
                navigate('/', { replace: true });
            } else {
                toast.error(res.error || 'Errore durante l\'aggiornamento della password');
            }
        } catch (error) {
            toast.error('Si è verificato un errore imprevisto');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white flex flex-col justify-center items-center p-6">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-md w-full"
            >
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-clip-50 rounded-2xl flex items-center justify-center mx-auto mb-6 transform -rotate-3">
                        <Lock className="text-clip-500 w-8 h-8" />
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Imposta Nuova Password</h1>
                    <p className="text-gray-500">
                        Scegli una password sicura per il tuo account.
                    </p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    {/* Password Field */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                            Nuova Password
                        </label>
                        <div className="relative group">
                            <Lock
                                className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${errors.password ? "text-red-400" : "text-gray-400 group-focus-within:text-clip-500"}`}
                                size={18}
                            />
                            <input
                                type={showPassword ? "text" : "password"}
                                {...register("password", { required: "La password è obbligatoria" })}
                                className={`w-full pl-11 pr-12 py-3.5 bg-gray-50 border rounded-xl outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400
                                    ${errors.password
                                        ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                                        : "border-gray-200 focus:border-clip-500 focus:ring-2 focus:ring-clip-200"
                                    }
                                `}
                                placeholder="Minimo 8 caratteri"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Confirm Password Field */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                            Conferma Password
                        </label>
                        <div className="relative group">
                            <Lock
                                className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${errors.confirmPassword ? "text-red-400" : "text-gray-400 group-focus-within:text-clip-500"}`}
                                size={18}
                            />
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                {...register("confirmPassword", {
                                    required: "Conferma la password",
                                    validate: val => val === password || "Le password non coincidono"
                                })}
                                className={`w-full pl-11 pr-12 py-3.5 bg-gray-50 border rounded-xl outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400
                                    ${errors.confirmPassword
                                        ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                                        : "border-gray-200 focus:border-clip-500 focus:ring-2 focus:ring-clip-200"
                                    }
                                `}
                                placeholder="Ripeti la password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                            >
                                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        {errors.confirmPassword && (
                            <p className="text-red-500 text-xs font-medium ml-1 flex items-center gap-1">
                                {errors.confirmPassword.message}
                            </p>
                        )}
                    </div>

                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full py-4 text-white font-bold rounded-xl shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2
                                ${loading
                                    ? "bg-gray-300 cursor-not-allowed shadow-none"
                                    : "bg-gray-900 hover:bg-black shadow-gray-200/50 hover:shadow-gray-300/50"
                                }
                            `}
                        >
                            {loading ? (
                                <Loader2 size={20} className="animate-spin" />
                            ) : (
                                <>
                                    Aggiorna Password
                                    <ArrowRight size={18} />
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};

export default ResetPassword;
